/**
 * Veterinary (quadruped) orientation model — the single source of truth for
 * how MPR panes and the 3D volume camera are aimed.
 *
 * Everything is expressed in the DICOM patient coordinate system (LPS). For
 * quadrupeds the app-wide mapping is: +X = patient left, +Y = dorsal,
 * +Z = cranial. Display conventions derived from that mapping:
 *
 *   - Transverse: viewed from cranial — dorsal up, patient left on viewer's right
 *   - Sagittal:   viewed from the patient's left — nose screen-LEFT, dorsal UP
 *                 (like a lateral radiograph)
 *   - Dorsal:     viewed from ventral — cranial up, patient left on viewer's
 *                 right (like a VD view)
 *   - 3D default: left-lateral view matching the sagittal pane (nose left,
 *                 dorsal up)
 *
 * ImageOrientationPatient is only as correct as the operator-entered
 * PatientPosition. When a vet scanner records a human position (e.g. HFS) for
 * a sternal or laterally recumbent animal, the tag-space axes rotate relative
 * to true anatomy — and tags cannot be trusted enough to auto-correct. The
 * AnatomicalBasis below is the manual escape hatch: it says where dorsal /
 * cranial / left REALLY point in volume space, and every camera (three panes
 * plus 3D) is re-derived from it so the whole viewer stays consistent.
 *
 * This module is pure math (no Cornerstone imports) so it stays unit-testable.
 */
// Quadruped anatomical axes in DICOM LPS space
export const LPS_LEFT = [1, 0, 0];
export const LPS_DORSAL = [0, 1, 0];
export const LPS_CRANIAL = [0, 0, 1];
/** Normalize -0 to 0 so orientation vectors compare byte-equal. */
function nz(n) {
    return n === 0 ? 0 : n;
}
export function cross(a, b) {
    return [
        nz(a[1] * b[2] - a[2] * b[1]),
        nz(a[2] * b[0] - a[0] * b[2]),
        nz(a[0] * b[1] - a[1] * b[0]),
    ];
}
export function negate(v) {
    return [nz(-v[0]), nz(-v[1]), nz(-v[2])];
}
export function dot(a, b) {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
/** Unit-length copy; returns null for a degenerate (zero-length) vector. */
export function normalize(v) {
    const len = Math.hypot(v[0], v[1], v[2]);
    if (!Number.isFinite(len) || len < 1e-9)
        return null;
    return [nz(v[0] / len), nz(v[1] / len), nz(v[2] / len)];
}
/**
 * How square two pane cameras are, in degrees, from their view normals.
 * 90 means perpendicular. Used to keep the three MPR panes mutually
 * orthogonal, which is what makes the crosshair lines cross at right angles.
 */
export function angleBetweenNormalsDeg(a, b) {
    const ua = normalize(a);
    const ub = normalize(b);
    if (!ua || !ub)
        return Number.NaN;
    const d = Math.min(1, Math.max(-1, dot(ua, ub)));
    return (Math.acos(d) * 180) / Math.PI;
}
/**
 * Largest deviation from square, in degrees, across the three pane pairs.
 * 0 for a correct MPR set.
 */
export function maxOrthogonalityErrorDeg(panes) {
    const pairs = [
        [panes.axial.viewPlaneNormal, panes.sagittal.viewPlaneNormal],
        [panes.axial.viewPlaneNormal, panes.coronal.viewPlaneNormal],
        [panes.sagittal.viewPlaneNormal, panes.coronal.viewPlaneNormal],
    ];
    return Math.max(...pairs.map(([a, b]) => Math.abs(angleBetweenNormalsDeg(a, b) - 90)));
}
/** Tag-space-is-correct basis: +X left, +Y dorsal, +Z cranial. */
export function defaultBasis() {
    return { left: [...LPS_LEFT], dorsal: [...LPS_DORSAL], cranial: [...LPS_CRANIAL] };
}
/**
 * Roll the basis about its own cranial (long) axis in exact 90° steps,
 * right-hand rule about `cranial`. In the transverse pane (viewed from
 * cranial) a +1 quarter turn rotates the displayed anatomy CLOCKWISE on
 * screen — that's the "the animal was actually rolled in the gantry"
 * correction.
 */
export function rollBasis(basis, quarterTurns) {
    const turns = ((quarterTurns % 4) + 4) % 4;
    let left = basis.left;
    let dorsal = basis.dorsal;
    for (let i = 0; i < turns; i++) {
        left = cross(basis.cranial, left);
        dorsal = cross(basis.cranial, dorsal);
    }
    return { left, dorsal, cranial: basis.cranial };
}
/**
 * Swap which end is the nose: a 180° rotation about the basis's own dorsal
 * axis (cranial and left both negate, dorsal is unchanged). Involution —
 * handedness is preserved, unlike negating cranial alone.
 */
export function flipNose(basis) {
    return { left: negate(basis.left), dorsal: basis.dorsal, cranial: negate(basis.cranial) };
}
/**
 * Derive every camera from an anatomical basis. viewPlaneNormal points from
 * the focal point TOWARD the camera (VTK convention), and
 * screenRight = viewUp × viewPlaneNormal.
 */
export function orientationsFromBasis(basis) {
    return {
        // Transverse — viewed from cranial: screenRight = dorsal × cranial = left
        axial: { viewPlaneNormal: [...basis.cranial], viewUp: [...basis.dorsal] },
        // Sagittal — viewed from the patient's left: screenRight = dorsal × left
        // = caudal, so the nose lands on screen-left
        sagittal: { viewPlaneNormal: [...basis.left], viewUp: [...basis.dorsal] },
        // Dorsal plane — viewed from ventral: screenRight = cranial × (-dorsal)
        // = left, so patient left lands on viewer's right
        coronal: { viewPlaneNormal: negate(basis.dorsal), viewUp: [...basis.cranial] },
        // 3D default — same aim as the sagittal pane (left-lateral, nose left)
        volume3d: { viewPlaneNormal: [...basis.left], viewUp: [...basis.dorsal] },
    };
}
/**
 * Square the three MPR panes to the VOLUME's own axes.
 *
 * The acquisition pane keeps its camera verbatim, so it still reproduces the
 * stored 2D image exactly. The other two panes are then snapped onto the
 * acquisition's other two axes rather than onto the patient's, so all three
 * cut along the slab and stay mutually perpendicular.
 *
 * Why this and not "put every pane on canonical anatomical axes": both keep
 * the crosshairs square, but anatomical axes cut an angled slab diagonally, so
 * the anatomy sits rotated inside each pane and the reader sees a crooked
 * image. Aligning to the acquisition keeps every pane rectangular, which is
 * what the legacy viewer does and what readers migrating from it expect.
 *
 * Each pane is snapped to the SIGNED axis nearest its canonical orientation,
 * so nothing flips: on an axis-aligned acquisition the acquisition axes are
 * the canonical axes and every pane comes back exactly as before.
 *
 * Returns null if the camera is degenerate; the caller then falls back to the
 * canonical set.
 */
export function alignPanesToAcquisition(acquiredPlane, acquiredCamera) {
    const normal = normalize(acquiredCamera.viewPlaneNormal);
    const rawUp = normalize(acquiredCamera.viewUp);
    if (!normal || !rawUp)
        return null;
    // Gram-Schmidt: the stored cosines are only orthogonal to within the
    // vendor's rounding, and a skewed frame would defeat the whole point.
    const proj = dot(rawUp, normal);
    const up = normalize([
        rawUp[0] - proj * normal[0],
        rawUp[1] - proj * normal[1],
        rawUp[2] - proj * normal[2],
    ]);
    if (!up)
        return null;
    const right = cross(up, normal);
    const axes = [right, up, normal];
    /** The signed axis pointing most nearly the same way as `v`. */
    const snap = (v, candidates) => {
        let best = 0;
        let bestScore = -Infinity;
        candidates.forEach((axis, i) => {
            const score = Math.abs(dot(v, axis));
            if (score > bestScore) {
                bestScore = score;
                best = i;
            }
        });
        const axis = candidates[best];
        return { axis: dot(v, axis) >= 0 ? axis : negate(axis), index: best };
    };
    const result = {};
    for (const pane of ['axial', 'sagittal', 'coronal']) {
        if (pane === acquiredPlane) {
            result[pane] = { viewPlaneNormal: [...normal], viewUp: [...up] };
            continue;
        }
        const canonical = VET_CANONICAL_ORIENTATIONS[pane];
        const picked = snap(canonical.viewPlaneNormal, axes);
        // viewUp has to lie in the pane, so it comes from the other two axes.
        const inPlane = axes.filter((_, i) => i !== picked.index);
        const pickedUp = snap(canonical.viewUp, inPlane);
        result[pane] = { viewPlaneNormal: picked.axis, viewUp: pickedUp.axis };
    }
    return result;
}
/** Canonical veterinary MPR pane cameras (tag space trusted). */
export const VET_CANONICAL_ORIENTATIONS = (({ axial, sagittal, coronal }) => ({ axial, sagittal, coronal }))(orientationsFromBasis(defaultBasis()));
export function identityOverride() {
    return { quarterTurns: 0, noseFlipped: false };
}
export function isIdentityOverride(override) {
    return override.quarterTurns % 4 === 0 && !override.noseFlipped;
}
export function basisFromOverride(override) {
    const rolled = rollBasis(defaultBasis(), override.quarterTurns);
    return override.noseFlipped ? flipNose(rolled) : rolled;
}
/**
 * Compose one more user roll (about the CURRENT cranial axis) onto an
 * override. After a nose flip the stored roll direction inverts
 * (flip ∘ roll = roll⁻¹ ∘ flip), which keeps
 * basisFromOverride(rollOverride(o, d)) === rollBasis(basisFromOverride(o), d).
 */
export function rollOverride(override, direction) {
    const step = override.noseFlipped ? -direction : direction;
    return {
        quarterTurns: (((override.quarterTurns + step) % 4) + 4) % 4,
        noseFlipped: override.noseFlipped,
    };
}
/** Compose a nose swap (about the CURRENT dorsal axis) onto an override. */
export function flipNoseOverride(override) {
    return { quarterTurns: override.quarterTurns, noseFlipped: !override.noseFlipped };
}
/**
 * Classify an acquisition's plane from the IOP direction cosines and build
 * the camera that reproduces the stored 2D image exactly
 * (screenRight = row direction, screenDown = column direction).
 */
export function classifyAcquisitionPlane(iop) {
    if (!iop || iop.length !== 6)
        return null;
    const rowCos = [iop[0], iop[1], iop[2]];
    const colCos = [iop[3], iop[4], iop[5]];
    const normal = cross(rowCos, colCos);
    // screenRight = viewUp × viewPlaneNormal, so reproducing the 2D stack
    // means viewPlaneNormal = -normal and viewUp = -colCos
    const camera = { viewPlaneNormal: negate(normal), viewUp: negate(colCos) };
    const ax = Math.abs(normal[0]);
    const ay = Math.abs(normal[1]);
    const az = Math.abs(normal[2]);
    const plane = az >= ax && az >= ay ? 'axial' : ax >= ay ? 'sagittal' : 'coronal';
    return { plane, camera };
}
/**
 * Named 3D views. `direction` is the unit vector from the focal point toward
 * the camera. Lateral and cranial/caudal views keep dorsal up; dorsal/ventral
 * (bird's-eye / VD-style) views keep cranial up, matching how VD radiographs
 * are hung.
 */
export const VET_CAMERA_ANGLES = {
    // screenRight = dorsal × left = caudal → nose on screen-left
    leftLateral: { direction: [1, 0, 0], viewUp: [0, 1, 0] },
    // screenRight = dorsal × right = cranial → nose on screen-right
    rightLateral: { direction: [-1, 0, 0], viewUp: [0, 1, 0] },
    // Looking down from above, cranial up
    dorsal: { direction: [0, 1, 0], viewUp: [0, 0, 1] },
    // Looking up from below, cranial up: patient left on viewer's right (VD)
    ventral: { direction: [0, -1, 0], viewUp: [0, 0, 1] },
    // Head-on, dorsal up: patient left on viewer's right (matches transverse)
    cranial: { direction: [0, 0, 1], viewUp: [0, 1, 0] },
    // From behind, dorsal up
    caudal: { direction: [0, 0, -1], viewUp: [0, 1, 0] },
};
